import type { JSX } from 'preact';
import { LIFE_ENERGY_MAX, LIFE_ENERGY_REGEN } from '../../game/account';
import { LIFE_SKILL_MAX, lifeXpToNext } from '../../game/Progression';
import { Bar, ItemIcon, Keycap, cssHue, itemColor, itemName } from '../haven/widgets';
import { SKILL_INFO, minutesToFull } from './data';
import { DigGame, type DigHint, FishingGame, HoldBarGame, RING_GOOD, RING_PERFECT, RingTapGame, type SwingGrade, type TapGrade } from './minigames';
import { type LifeSession, closeSession, digPick, gatherPress, gatherRelease, lifeGame, lifeSession, lifeTick } from './session';

/**
 * The gathering overlay: a compact panel above the combat HUD with the
 * mini-game, a Life Energy readout and the results card. Driven every frame
 * by `lifeTick` while a session runs.
 */

const pct = (v: number) => `${(Math.max(0, Math.min(1, v)) * 100).toFixed(2)}%`;

const TAP_LABEL: Record<TapGrade, string> = { perfect: 'Perfect!', good: 'Good', miss: 'Miss' };
const SWING_LABEL: Record<SwingGrade, string> = { perfect: 'Perfect!', success: 'Clean hit', weak: 'Weak swing', miss: 'Miss' };

/** Tells the player which way they missed the zone. */
function swingLabel(g: HoldBarGame, grade: SwingGrade): string {
  if (grade === 'perfect' || grade === 'success') return SWING_LABEL[grade];
  if (g.lastFill >= 1) return 'Overswing!';
  if (g.lastFill > g.zone[1]) return 'Too late';
  return grade === 'weak' ? 'Too early' : 'Barely a tap';
}
const HINT_LABEL: Record<DigHint, string> = { found: 'Relic!', hot: 'Hot', warm: 'Warm', cold: 'Cold' };

// ---------------------------------------------------------------- glyphs

function ToolGlyph({ kind }: { kind: string }) {
  const common = { fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round' as const, 'stroke-linejoin': 'round' as const };
  switch (kind) {
    case 'timber':
      return (
        <svg viewBox="0 0 24 24" class="ls-glyph">
          <path d="M5 21 L15 7" {...common} />
          <path d="M12.5 3.5 L20.5 7 L17.5 12.5 L12.5 10 Z" {...common} fill="currentColor" fill-opacity="0.25" />
        </svg>
      );
    case 'ore':
      return (
        <svg viewBox="0 0 24 24" class="ls-glyph">
          <path d="M6 21 L14 10" {...common} />
          <path d="M5 8 Q12 2 21 8" {...common} stroke-width={2.6} />
        </svg>
      );
    case 'fish':
      return (
        <svg viewBox="0 0 24 24" class="ls-glyph">
          <path d="M3 12 C7 6 14 6 18 12 C14 18 7 18 3 12 Z" {...common} fill="currentColor" fill-opacity="0.25" />
          <path d="M18 12 L22 8 L22 16 Z" {...common} />
          <circle cx="7.5" cy="11" r="0.9" fill="currentColor" />
        </svg>
      );
    case 'dig':
      return (
        <svg viewBox="0 0 24 24" class="ls-glyph">
          <path d="M12 3 L12 14 M9 3 L15 3" {...common} />
          <path d="M8 14 L16 14 L14.5 21 L9.5 21 Z" {...common} fill="currentColor" fill-opacity="0.25" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" class="ls-glyph">
          <path d="M12 3 C18 7 18.5 15 12 21 C5.5 15 6 7 12 3 Z" {...common} fill="currentColor" fill-opacity="0.25" />
          <path d="M12 6 L12 19" {...common} stroke-width={1.4} />
        </svg>
      );
  }
}

function Pips({ grades, total }: { grades: string[]; total: number }) {
  return (
    <div class="ls-pips">
      {Array.from({ length: total }, (_, i) => (
        <i key={i} class={grades[i] ?? (i === grades.length ? 'cur' : '')} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- stages

function RingStage({ g }: { g: RingTapGame }) {
  const R = 74;
  const flash = g.pause > 0 ? g.lastGrade : null;
  return (
    <div class="ls-game ls-ring-game">
      <svg class="ls-ring" viewBox="-80 -80 160 160">
        <defs>
          <radialGradient id="lsRingBg" cx="50%" cy="45%" r="60%">
            <stop offset="0" stop-color="#23321f" />
            <stop offset="1" stop-color="#0b0d0b" />
          </radialGradient>
        </defs>
        <circle r="77" fill="url(#lsRingBg)" stroke="#3a3d42" />
        <circle r={g.target * R} fill="none" stroke="var(--ls-hue)" stroke-opacity="0.24" stroke-width={RING_GOOD * 2 * R} />
        <circle r={g.target * R} fill="none" stroke="#ffe7a0" stroke-opacity="0.9" stroke-width={RING_PERFECT * 2 * R} />
        {flash ? (
          <circle r={Math.max(0.5, g.lastRadius * R)} fill="none" class={`ls-ring-hit ${flash}`} stroke-width="3" />
        ) : (
          <circle r={Math.max(0.5, g.radius * R)} fill="none" stroke="#f4fff0" stroke-width="2.4" class="ls-ring-live" />
        )}
        <g transform="translate(-12 -12)" class="ls-ring-core">
          <path d="M12 3 C18 7 18.5 15 12 21 C5.5 15 6 7 12 3 Z" fill="var(--ls-hue)" fill-opacity="0.35" stroke="var(--ls-hue)" stroke-width="1.5" />
          <path d="M12 6 L12 19" stroke="var(--ls-hue)" stroke-width="1.2" />
        </g>
      </svg>
      <div class="ls-side">
        <div class="ls-caption">Round {Math.min(g.round + 1, g.rounds)} / {g.rounds}</div>
        <Pips grades={g.results} total={g.rounds} />
        <div key={g.results.length} class={`ls-flash ${flash ?? 'idle'}`}>
          {flash ? TAP_LABEL[flash] : 'Watch the ring…'}
        </div>
      </div>
    </div>
  );
}

function BarStage({ g, kind }: { g: HoldBarGame; kind: string }) {
  const flash = g.pause > 0 ? g.lastGrade : null;
  const fill = g.pause > 0 ? g.lastFill : g.fill;
  return (
    <div class="ls-game ls-bar-game">
      <div class="ls-bar-top">
        <span class="ls-tool">
          <ToolGlyph kind={kind} />
        </span>
        <span class="ls-caption">
          Swing {Math.min(g.swing + 1, g.swings)} / {g.swings}
        </span>
        <Pips grades={g.results} total={g.swings} />
      </div>
      <div class={`ls-meter${g.holding ? ' charging' : ''}`}>
        <div class="ls-meter-fill" style={{ width: pct(fill) }} />
        <div class="ls-meter-zone" style={{ left: pct(g.zone[0]), width: pct(g.zone[1] - g.zone[0]) }} />
        <div class="ls-meter-core" style={{ left: pct(g.core[0]), width: pct(g.core[1] - g.core[0]) }} />
        <div class={`ls-meter-tick ${flash ?? ''}`} style={{ left: pct(fill) }} />
      </div>
      <div key={g.results.length} class={`ls-flash ${flash ?? 'idle'}`}>
        {flash ? swingLabel(g, flash) : g.holding ? 'Release in the gold…' : 'Ready — hold to swing'}
      </div>
    </div>
  );
}

function FishStage({ g }: { g: FishingGame }) {
  if (g.phase !== 'reel') {
    const bite = g.phase === 'bite';
    return (
      <div class="ls-game ls-fish-wait">
        <svg class={`ls-water${bite ? ' bite' : ''}`} viewBox="0 0 300 96" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lsWater" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#16364a" />
              <stop offset="1" stop-color="#07131b" />
            </linearGradient>
          </defs>
          <rect width="300" height="96" fill="url(#lsWater)" />
          <ellipse class="ls-ripple r1" cx="150" cy="58" rx="26" ry="6" />
          <ellipse class="ls-ripple r2" cx="150" cy="58" rx="26" ry="6" />
          <path d="M150 0 L150 50" stroke="#cfd8df" stroke-opacity="0.5" stroke-width="1" />
          <g class="ls-bobber">
            <circle cx="150" cy="54" r="6.5" fill="#f4f4f2" />
            <path d="M143.5 54 A6.5 6.5 0 0 0 156.5 54 Z" fill="#e5484d" />
          </g>
        </svg>
        {bite ? <div class="ls-bang">!</div> : <div class="ls-caption ls-wait-text">Waiting for a bite…</div>}
      </div>
    );
  }
  const left = g.zoneCenter - g.zoneWidth / 2;
  const status = g.inZone ? 'Steady — keep it in the current' : g.tension > g.zoneCenter ? 'Too tight! Ease off' : 'Line slack! Reel in';
  return (
    <div class="ls-game ls-reel">
      <div class="ls-bar-top">
        <span class="ls-tool">
          <ToolGlyph kind="fish" />
        </span>
        <span class={`ls-caption${g.rare ? ' rare' : ''}`}>{g.rare ? 'Something big is on the line!' : 'Fish on!'}</span>
      </div>
      <div class={`ls-tension${g.inZone ? ' in' : ' out'}`}>
        <div class={`ls-tension-zone${g.rare ? ' rare' : ''}`} style={{ left: pct(left), width: pct(g.zoneWidth) }} />
        <div class="ls-tension-mark" style={{ left: pct(g.tension) }} />
        <span class="ls-tension-l">slack</span>
        <span class="ls-tension-r">taut</span>
      </div>
      <div class="ls-catch">
        <i style={{ width: pct(g.progress) }} />
      </div>
      <div class={`ls-flash ${g.inZone ? 'good' : 'miss'}`}>
        {status}
        {!g.inZone && <span class="ls-escape" style={{ width: pct(1 - g.outside / 1.4) }} />}
      </div>
    </div>
  );
}

function DigStage({ g }: { g: DigGame }) {
  const cells: JSX.Element[] = [];
  for (let y = 0; y < g.size; y++) {
    for (let x = 0; x < g.size; x++) {
      const dug = g.dug.find((d) => d.x === x && d.y === y);
      const offer = g.offers.findIndex((o) => o.x === x && o.y === y);
      if (dug)
        cells.push(
          <div key={`${x},${y}`} class={`ls-cell dug ${dug.hint}`}>
            {HINT_LABEL[dug.hint]}
          </div>,
        );
      else if (offer >= 0)
        cells.push(
          <button key={`${x},${y}`} class="ls-cell offer" onClick={() => digPick(offer)}>
            {offer + 1}
          </button>,
        );
      else cells.push(<div key={`${x},${y}`} class="ls-cell" />);
    }
  }
  const last = g.dug[g.dug.length - 1];
  return (
    <div class="ls-game ls-dig">
      <div class="ls-dig-grid">{cells}</div>
      <div class="ls-side">
        <div class="ls-caption">
          Dig {Math.min(g.turn + 1, g.turns)} / {g.turns}
        </div>
        <div key={g.dug.length} class={`ls-flash ${g.pause > 0 && last ? last.hint : 'idle'}`}>
          {g.pause > 0 && last ? HINT_LABEL[last.hint] : 'Choose a marked spot'}
        </div>
        <div class="ls-legend">
          <span class="hot">Hot</span> next to the relic
          <br />
          <span class="warm">Warm</span> two spaces away
          <br />
          <span class="cold">Cold</span> further off
        </div>
      </div>
    </div>
  );
}

function ResultView({ s }: { s: LifeSession }) {
  const r = s.result;
  const game = lifeGame.current;
  if (!r) return null;
  const info = SKILL_INFO[s.node.skill];
  const ls = game?.progression?.char.lifeSkills[s.node.skill];
  const lvl = ls?.level ?? 1;
  const maxed = lvl >= LIFE_SKILL_MAX;
  const failText =
    s.mini instanceof FishingGame
      ? s.mini.outcome === 'early'
        ? 'You struck too soon and spooked the fish.'
        : s.mini.outcome === 'late'
          ? 'The fish stole the bait.'
          : 'The line went slack and the fish slipped away.'
      : 'Nothing worth keeping.';
  return (
    <div class="ls-result">
      <div class={`ls-grade${r.failed ? ' fail' : r.rare ? ' rare' : ''}`}>{r.grade}</div>
      <div class="ls-loot">
        {r.items.length ? (
          r.items.map((it) => (
            <div key={it.id} class={`ls-loot-row${it.id === s.node.rare ? ' rare' : ''}`}>
              <ItemIcon id={it.id} size={30} />
              <span style={{ color: itemColor(it.id) }}>{itemName(it.id)}</span>
              <b>×{it.count}</b>
            </div>
          ))
        ) : (
          <div class="ls-fail">{failText}</div>
        )}
      </div>
      <div class="ls-xp">
        <span>
          +{r.xp} {info.name} XP
        </span>
        {r.levelUp ? <span class="ls-lvup">Level {r.levelUp}!</span> : null}
        <Bar tone="green" value={maxed ? 1 : (ls?.xp ?? 0) / lifeXpToNext(lvl)} label={maxed ? 'Mastered' : `Lv ${lvl}`} />
      </div>
    </div>
  );
}

function Stage({ s }: { s: LifeSession }) {
  const g = s.mini;
  if (g instanceof RingTapGame) return <RingStage g={g} />;
  if (g instanceof HoldBarGame) return <BarStage g={g} kind={s.node.kind} />;
  if (g instanceof FishingGame) return <FishStage g={g} />;
  if (g instanceof DigGame) return <DigStage g={g} />;
  return null;
}

function Hint({ s }: { s: LifeSession }) {
  if (s.phase === 'result')
    return (
      <span class="ls-hint">
        Continue <Keycap k="G" />
      </span>
    );
  const g = s.mini;
  if (g instanceof RingTapGame)
    return (
      <span class="ls-hint">
        <Keycap k="G" /> as the rings meet
      </span>
    );
  if (g instanceof HoldBarGame)
    return (
      <span class="ls-hint">
        Hold <Keycap k="G" /> · release in gold
      </span>
    );
  if (g instanceof FishingGame)
    return g.phase === 'reel' ? (
      <span class="ls-hint">
        Hold <Keycap k="G" /> to reel in
      </span>
    ) : g.phase === 'bite' ? (
      <span class="ls-hint now">
        Strike! <Keycap k="G" />
      </span>
    ) : (
      <span class="ls-hint">
        <Keycap k="G" /> when it bites
      </span>
    );
  return (
    <span class="ls-hint">
      Dig <Keycap k="1" /> <Keycap k="2" /> <Keycap k="3" /> or click
    </span>
  );
}

function EnergyReadout({ energy, cost }: { energy: number; cost: number }) {
  const full = energy >= LIFE_ENERGY_MAX;
  return (
    <div class="ls-energy" title={full ? 'Life Energy is full' : `Regenerates ${LIFE_ENERGY_REGEN} per minute — full in ${minutesToFull(energy)} min`}>
      <svg viewBox="0 0 24 24" class="ls-bolt">
        <path d="M13 2 L5 14 L11 14 L10 22 L19 9 L13 9 Z" />
      </svg>
      <div class="ls-energy-bar">
        <i style={{ width: pct(energy / LIFE_ENERGY_MAX) }} />
      </div>
      <span class="ls-energy-n">
        {energy.toLocaleString('en-US')}
        <small>/{LIFE_ENERGY_MAX.toLocaleString('en-US')}</small>
      </span>
      <span class="ls-energy-cost" title="Life Energy spent on this attempt">
        −{cost}
      </span>
    </div>
  );
}

export function LifeSkillOverlay() {
  void lifeTick.value;
  const s = lifeSession.value;
  const game = lifeGame.current;
  if (!s || !game) return null;
  const info = SKILL_INFO[s.node.skill];
  const energy = Math.floor(game.progression?.lifeEnergy() ?? 0);
  const lvl = game.progression?.char.lifeSkills[s.node.skill]?.level ?? 1;
  const interactive = s.phase === 'play' && !(s.mini instanceof DigGame);
  const down = (e: PointerEvent) => {
    if (e.button !== 0) return;
    gatherPress();
  };
  return (
    <div class={`ls-overlay panel ${s.node.kind}`} style={{ '--ls-hue': cssHue(info.hue) }} onPointerDown={(e) => e.stopPropagation()}>
      <div class="ls-head">
        <span class="ls-head-ic">
          <ToolGlyph kind={s.node.kind} />
        </span>
        <span class="ls-skill">{info.name}</span>
        <span class="ls-node">{s.node.name}</span>
        <span class="ls-lv">Lv {lvl}</span>
        <button class="panel__close" aria-label="Stop gathering" onClick={() => closeSession(game)}>
          ✕
        </button>
      </div>
      <div
        class={`ls-stage${interactive ? ' clickable' : ''}`}
        onPointerDown={interactive ? down : undefined}
        onPointerUp={interactive ? () => gatherRelease() : undefined}
        onPointerLeave={interactive ? () => gatherRelease() : undefined}
      >
        {s.phase === 'result' ? <ResultView s={s} /> : <Stage s={s} />}
      </div>
      <div class="ls-foot">
        <EnergyReadout energy={energy} cost={s.cost} />
        {s.phase === 'result' ? (
          <button class="btn btn--gold ls-continue" onClick={() => closeSession(game)}>
            Continue <Keycap k="G" />
          </button>
        ) : (
          <Hint s={s} />
        )}
      </div>
    </div>
  );
}
